import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ChevronLeftIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import Drawer from '../Drawer';
import { getAllowedUserPermissions } from '../../helpers/common';
import { CHILDREN_TAB_PROFIT_AND_LOSS } from '../../consts/management/users/users';
import { LoaderSpinner } from '../LoaderSpinner';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import {
  ProfitAndLossNewLoadingKeys,
  getProfitAndLossTables,
  createProfitAndLossTableItem,
  updateProfitAndLossTableItem,
  deleteProfitAndLossTableItem,
  updateProfitAndLossTablePositions,
  getProfitAndLossKeys,
  getBudgetLineItemsForCalculation,
} from '../../slices/quickbooks/profitAndLossNewSlice';
import { getBudgetSettings } from '../../slices/quickbooks/budgetSlice';
import { EditableTableItem } from './EditableTableItem';
import { TableItemCreateEditSidebar } from './TableItemCreateEditSidebar';
import { ConfrimModal } from '../ConfrimModal';
import { toast } from 'react-toastify';
import { ProfitAndLossTableItemDeleteInfoModal } from './ProfitAndLossTableItemDeleteInfoModal';
import { SelectDropdown } from '../SelectDropdown';
import {
  fetchProfitAndLossTotalNames,
  ProfitAndLossTotalNamesLoadingKeys,
  saveProfitAndLossTotalNames,
} from '../../slices/profitAndLossTotalNamesSlice';

const TABLE_KEYS = [
  'summary',
  'total-cost-of-goods-sold',
  'total-operating-expenses',
  'net-income-summary',
];

const TABLE_LABELS = {
  summary: 'Summary',
  'revenue-breakdown': 'Revenue Breakdown',
  'contribution-margin': 'Contribution Margin',
  'total-cost-of-goods-sold': 'Total Cost of Goods Sold',
  'total-operating-expenses': 'Total Operating Expenses',
  'net-income-summary': 'Net Income Summary',
};

const TABLE_ORDER = TABLE_KEYS.reduce((acc, tableKey, index) => {
  acc[tableKey] = index;
  return acc;
}, {});

const TOTAL_NAME_FIELDS = [
  'revenue',
  'contribution_margin',
  'net_income',
  'ad_spend',
  'mer',
  'beroas',
];

const emptyTotalNameSettings = {
  revenue: null,
  contribution_margin: null,
  net_income: null,
  ad_spend: null,
  mer: null,
  beroas: null,
};

const normalizeTotalNameId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatTotalNameFieldLabel = (fieldKey) => {
  if (fieldKey === 'mer' || fieldKey === 'beroas') {
    return fieldKey.toUpperCase();
  }

  return String(fieldKey || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const ProfitAndLossSettingsSidebar = ({
  isOpen,
  onClose,
  marginFromSidebar,
}) => {
  const dispatch = useDispatch();
  const { currentUser, loading: authLoading } = useSelector((state) => state.auth);
  const { budgetSettings } = useSelector((state) => state.budget);
  const { 
    profitAndLossTables, 
    profitAndLossKeys,
    budgetLineItemsForCalculation,
    loadingKeys: profitAndLossLoadingKeys 
  } = useSelector((state) => state.profitAndLossNew);
  const {
    profitAndLossTotalNames,
    loadingKeys: profitAndLossTotalNamesLoadingKeys,
  } = useSelector((state) => state.profitAndLossTotalNames);

  const userPermissions = currentUser?.permissions || [];
  const { childIdToAccess } = getAllowedUserPermissions({ permissions: userPermissions });
  const routesAccess = childIdToAccess.get(CHILDREN_TAB_PROFIT_AND_LOSS);
  const hasEditAccess = routesAccess === 'edit';

  const [selectedTableKey, setSelectedTableKey] = useState(null);
  const [orderedItems, setOrderedItems] = useState({});
  const [isLocalOrdering, setIsLocalOrdering] = useState({});
  const [itemEditorMode, setItemEditorMode] = useState(null); // null | 'create' | 'edit'
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemEditorCanSave, setItemEditorCanSave] = useState(false);
  const [itemEditorSubmit, setItemEditorSubmit] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteInfoModalOpen, setDeleteInfoModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [localTotalNames, setLocalTotalNames] = useState(null);
  const [hasEditedTotalNames, setHasEditedTotalNames] = useState(false);
  const isTotalNamesFetching = profitAndLossTotalNamesLoadingKeys.includes(
    ProfitAndLossTotalNamesLoadingKeys.fetch
  );
  const isTotalNamesSaving = profitAndLossTotalNamesLoadingKeys.includes(
    ProfitAndLossTotalNamesLoadingKeys.save
  );

  // Fetch tables when opening (edit access only)
  useEffect(() => {
    if (!isOpen || !hasEditAccess) return;
    dispatch(getProfitAndLossTables({}));
    // Fetch keys and budget items for calculations
    dispatch(getProfitAndLossKeys({}));
    dispatch(getBudgetLineItemsForCalculation({}));
    dispatch(getBudgetSettings({}));
    dispatch(fetchProfitAndLossTotalNames({}));
  }, [dispatch, isOpen, hasEditAccess]);

  // Initialize ordered items when tables are loaded
  useEffect(() => {
    if (!isOpen) return;
    TABLE_KEYS.forEach((tableKey) => {
      const tables = profitAndLossTables[tableKey] || [];
      if (tables.length > 0) {
        setOrderedItems((prev) => {
          // Only update if not currently being reordered
          if (isLocalOrdering[tableKey]) return prev;
          // Only update if the data actually changed
          const currentItems = prev[tableKey] || [];
          if (currentItems.length === tables.length && 
              currentItems.every((item, idx) => item.id === tables[idx]?.id)) {
            return prev;
          }
          return {
            ...prev,
            [tableKey]: [...tables],
          };
        });
      }
    });
  }, [isOpen, profitAndLossTables]);

  // Reset when closing
  useEffect(() => {
    if (isOpen) return;
    setSelectedTableKey(null);
    setOrderedItems({});
    setIsLocalOrdering({});
    setItemEditorMode(null);
    setItemToEdit(null);
    setItemEditorCanSave(false);
    setItemEditorSubmit(null);
    setDeleteModalOpen(false);
    setDeleteInfoModalOpen(false);
    setItemToDelete(null);
    setLocalTotalNames(null);
    setHasEditedTotalNames(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isTotalNamesFetching || hasEditedTotalNames) return;

    const nextSettings = TOTAL_NAME_FIELDS.reduce((acc, fieldKey) => {
      acc[fieldKey] = normalizeTotalNameId(profitAndLossTotalNames?.[fieldKey]);
      return acc;
    }, { ...emptyTotalNameSettings });

    setLocalTotalNames((prev) => {
      const hasChanges = TOTAL_NAME_FIELDS.some(
        (fieldKey) => normalizeTotalNameId(prev?.[fieldKey]) !== nextSettings[fieldKey]
      );

      return prev === null || hasChanges ? nextSettings : prev;
    });
  }, [isOpen, isTotalNamesFetching, hasEditedTotalNames, profitAndLossTotalNames]);

  const moveItem = useCallback(
    (tableKey, dragIndex, hoverIndex) => {
      if (!hasEditAccess) return;
      setIsLocalOrdering((prev) => ({ ...prev, [tableKey]: true }));
      setOrderedItems((prev) => {
        const updated = [...(prev[tableKey] || [])];
        const [removed] = updated.splice(dragIndex, 1);
        updated.splice(hoverIndex, 0, removed);
        return { ...prev, [tableKey]: updated };
      });
    },
    [hasEditAccess]
  );

  const persistOrderIfChanged = useCallback(
    async (tableKey) => {
      if (!hasEditAccess || !orderedItems[tableKey]?.length) return;

      const positionUpdates = orderedItems[tableKey].map((item, index) => ({
        id: item.id,
        position: index + 1,
      }));

      try {
        await dispatch(updateProfitAndLossTablePositions({ positionUpdates })).unwrap();
        await dispatch(getProfitAndLossTables({}));
        toast.success('Items reordered successfully', { position: 'bottom-right' });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLocalOrdering((prev) => ({ ...prev, [tableKey]: false }));
      }
    },
    [dispatch, orderedItems, hasEditAccess]
  );

  const openCreateItemEditor = () => {
    if (!selectedTableKey) return;
    setItemEditorMode('create');
    setItemToEdit(null);
  };

  const openEditItemEditor = (item) => {
    setItemEditorMode('edit');
    setItemToEdit(item || null);
  };

  const closeItemEditor = () => {
    setItemEditorMode(null);
    setItemToEdit(null);
  };

  const handleSaveItem = useCallback(async ({ item, calculation_profit_and_loss, calculation_budget }) => {
    try {
      if (itemEditorMode === 'edit' && itemToEdit?.id) {
        await dispatch(
          updateProfitAndLossTableItem({
            id: itemToEdit.id,
            item,
            calculation_profit_and_loss,
            calculation_budget,
          })
        ).unwrap();
        toast.success('Item updated successfully', { position: 'bottom-right' });
      } else if (selectedTableKey) {
        await dispatch(
          createProfitAndLossTableItem({
            table_key: selectedTableKey,
            item,
            calculation_profit_and_loss,
            calculation_budget,
          })
        ).unwrap();
        toast.success('Item created successfully', { position: 'bottom-right' });
      }
      closeItemEditor();
      await dispatch(getProfitAndLossTables({}));
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Could not save item', { position: 'bottom-right' });
    }
  }, [dispatch, itemEditorMode, itemToEdit, selectedTableKey]);

  const handleRegisterItemSubmit = useCallback((fn) => {
    setItemEditorSubmit(() => fn);
  }, []);

  const handleItemCanSaveChange = useCallback((canSave) => {
    setItemEditorCanSave(canSave);
  }, []);

  const handleDeleteClick = (item) => {
    if (item?.is_used_in_settings) {
      setItemToDelete(item);
      setDeleteInfoModalOpen(true);
      return;
    }
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteDisabledClick = (item) => {
    setItemToDelete(item);
    setDeleteInfoModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete?.id) return;
    try {
      await dispatch(deleteProfitAndLossTableItem({ id: itemToDelete.id })).unwrap();
      await dispatch(getProfitAndLossTables({}));
      setDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success('Item deleted successfully', { position: 'bottom-right' });
    } catch (error) {
      console.error(error);
      setDeleteModalOpen(false);
      if (error?.forecast_settings_metric_keys?.length > 0 || error?.profit_and_loss_total_name_keys?.length > 0) {
        setItemToDelete((prev) => ({
          ...(prev || {}),
          forecast_settings_metric_labels: error?.forecast_settings_metric_labels || [],
          profit_and_loss_total_name_labels: error?.profit_and_loss_total_name_labels || [],
        }));
        setDeleteInfoModalOpen(true);
      } else {
        setItemToDelete(null);
        toast.error('Could not delete item', { position: 'bottom-right' });
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handleCloseDeleteInfo = () => {
    setDeleteInfoModalOpen(false);
    setItemToDelete(null);
  };

  const handleSaveTotalNames = async () => {
    if (!localTotalNames) return;

    try {
      await dispatch(saveProfitAndLossTotalNames(localTotalNames)).unwrap();
      await dispatch(getProfitAndLossTables({})).unwrap();
      setHasEditedTotalNames(false);
    } catch (error) {
      console.error(error);
    }
  };

  const selectTable = (tableKey) => {
    setSelectedTableKey(tableKey);
  };

  const drawerTitle = useMemo(() => {
    if (itemEditorMode === 'edit') {
      return 'Edit Table Item';
    }
    if (itemEditorMode === 'create') {
      return 'Create Table Item';
    }
    if (selectedTableKey) {
      return `Profit & Loss Settings • ${TABLE_LABELS[selectedTableKey] || selectedTableKey}`;
    }
    return 'Profit & Loss Settings';
  }, [selectedTableKey, itemEditorMode]);

  const isTablesLoading = useCallback(() => {
    return profitAndLossLoadingKeys.includes(ProfitAndLossNewLoadingKeys.getProfitAndLossTables);
  }, [profitAndLossLoadingKeys]);

  const isSavingItem =
    profitAndLossLoadingKeys.includes(ProfitAndLossNewLoadingKeys.createProfitAndLossTableItem) ||
    profitAndLossLoadingKeys.includes(ProfitAndLossNewLoadingKeys.updateProfitAndLossTableItem);

  const isDeletingItem = profitAndLossLoadingKeys.includes(
    ProfitAndLossNewLoadingKeys.deleteProfitAndLossTableItem
  );

  const showActionButtons = itemEditorMode !== null;
  const submitDisabled = showActionButtons ? !itemEditorCanSave : false;

  const handleDrawerSubmit = () => {
    if (showActionButtons) return itemEditorSubmit?.();
  };

  const handleDrawerClose = () => {
    if (showActionButtons) return closeItemEditor();
    if (selectedTableKey) return selectTable(null);
    onClose?.();
  };

  const profitAndLossItemOptions = useMemo(() => {
    return Object.keys(profitAndLossTables || {})
      .flatMap((tableKey) => {
        const tableItems = profitAndLossTables?.[tableKey] || [];
        return tableItems.map((item) => ({
          value: normalizeTotalNameId(item.id),
          label: `${TABLE_LABELS[tableKey] || tableKey} / ${item?.item?.name || 'Unnamed'}`,
          groupLabel: TABLE_LABELS[tableKey] || tableKey,
          tableKey,
          position: Number(item?.position) || 0,
        }));
      })
      .sort((a, b) => {
        const orderA = TABLE_ORDER[a.tableKey] ?? Number.MAX_SAFE_INTEGER;
        const orderB = TABLE_ORDER[b.tableKey] ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return (a.position || 0) - (b.position || 0);
      });
  }, [profitAndLossTables]);

  const hasAllTotalNameSelections = TOTAL_NAME_FIELDS.every((fieldKey) => normalizeTotalNameId(localTotalNames?.[fieldKey]));
  const hasDirtyTotalNames = TOTAL_NAME_FIELDS.some(
    (fieldKey) =>
      normalizeTotalNameId(localTotalNames?.[fieldKey]) !==
      normalizeTotalNameId(profitAndLossTotalNames?.[fieldKey])
  );

  if (authLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title="Profit & Loss Settings" width="900px" size="xl">
        <div className="p-6">
          <LoaderSpinner colorSpinner="black" width="70" height="70" border="3" />
        </div>
      </Drawer>
    );
  }

  if (!hasEditAccess) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title="Profit & Loss Settings" width="900px" size="xl">
        <div className="p-6">No access</div>
      </Drawer>
    );
  }

  const currentTableItems =
    selectedTableKey && (isLocalOrdering[selectedTableKey] ? orderedItems[selectedTableKey] : profitAndLossTables[selectedTableKey] || []);

  return (
    <DndProvider backend={HTML5Backend}>
      <Drawer
        isOpen={isOpen}
        onClose={handleDrawerClose}
        title={drawerTitle}
        showActionButtons={showActionButtons}
        submitButtonText={isSavingItem ? 'Saving...' : 'Save'}
        submitDisabled={submitDisabled}
        onSubmit={handleDrawerSubmit}
        width="900px"
        size="xl"
        marginFromSidebar={marginFromSidebar}
      >
        <div className="relative">
          {(isTablesLoading() || isTotalNamesFetching) && <OverlayGrayBackground />}
          <div className="px-6 py-4">
            {selectedTableKey && !showActionButtons && (
              <div className="mb-4 flex items-center justify-between">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  onClick={() => selectTable(null)}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                  Back
                </button>
              </div>
            )}

            {showActionButtons && (
              <div className="mb-4 flex items-center justify-between">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  onClick={closeItemEditor}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                  Back
                </button>
              </div>
            )}

            {!selectedTableKey && !showActionButtons && (
              <div>
                <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold">Total Cards</h2>
                      <p className="mt-1 text-sm text-gray-500">
                        Link each total card to one Profit &amp; Loss table item.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveTotalNames}
                      disabled={!hasAllTotalNameSelections || !hasDirtyTotalNames || isTotalNamesSaving}
                      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {isTotalNamesSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {TOTAL_NAME_FIELDS.map((fieldKey) => (
                      <div key={fieldKey}>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {formatTotalNameFieldLabel(fieldKey)}
                        </label>
                        <SelectDropdown
                          options={profitAndLossItemOptions}
                          value={localTotalNames?.[fieldKey]}
                          onChange={(value) => {
                            setHasEditedTotalNames(true);
                            setLocalTotalNames((prev) => ({
                              ...(prev || emptyTotalNameSettings),
                              [fieldKey]: normalizeTotalNameId(value),
                            }));
                          }}
                          placeholder="Select a table item"
                          selectClassname="!bg-white"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <h2 className="text-lg font-semibold">Select a Table</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose a table to manage its items and configuration.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {TABLE_KEYS.map((tableKey) => {
                    const items = profitAndLossTables[tableKey] || [];
                    return (
                      <button
                        key={tableKey}
                        type="button"
                        className="border border-gray-200 rounded-lg bg-white px-4 py-3 flex items-center justify-between hover:bg-gray-50 text-left"
                        onClick={() => selectTable(tableKey)}
                      >
                        <div>
                          <div className="text-base font-medium text-gray-900">
                            {TABLE_LABELS[tableKey] || tableKey}
                          </div>
                          <div className="text-sm text-gray-500">
                            {items.length} {items.length === 1 ? 'item' : 'items'}
                          </div>
                        </div>
                        <PencilIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedTableKey && !showActionButtons && (
              <div>
                <div className="mb-4">
                  <div>
                    <h2 className="text-lg font-semibold">
                      {(() => {
                        return TABLE_LABELS[selectedTableKey] || selectedTableKey;
                      })()}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Manage items for this table. Drag to reorder.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  {currentTableItems && currentTableItems.length > 0 ? (
                    currentTableItems.map((item, index) => (
                      <EditableTableItem
                        key={item.id}
                        item={item}
                        index={index}
                        tableKey={selectedTableKey}
                        hasEditAccess={hasEditAccess}
                        moveItem={moveItem}
                        onDrop={persistOrderIfChanged}
                        onEditClick={openEditItemEditor}
                        onDeleteClick={handleDeleteClick}
                        onDeleteDisabledClick={handleDeleteDisabledClick}
                        isSaving={isSavingItem}
                      />
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 py-4 text-center">
                      No items yet. Click "Add Item" to create one.
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={openCreateItemEditor}
                    className="text-base bg-primary text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors flex items-center gap-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Item
                  </button>
                </div>
              </div>
            )}

            {showActionButtons && (
              <TableItemCreateEditSidebar
                embedded={true}
                isOpen={true}
                onClose={closeItemEditor}
                mode={itemEditorMode}
                item={itemToEdit}
                profitAndLossKeys={profitAndLossKeys}
                budgetLineItems={budgetLineItemsForCalculation}
                categoriesToRender={budgetSettings?.categories || []}
                onSave={handleSaveItem}
                isSaving={isSavingItem}
                onRegisterSubmit={handleRegisterItemSubmit}
                onCanSaveChange={handleItemCanSaveChange}
              />
            )}
          </div>

          {/* Modals */}
          <ConfrimModal
            isOpen={deleteModalOpen}
            title={`Are you sure you want to delete "${itemToDelete?.item?.name || 'this item'}"?`}
            confirmButtonTitle="Delete"
            isLoading={isDeletingItem}
            submitClick={handleConfirmDelete}
            cancelClick={handleCancelDelete}
          />
          <ProfitAndLossTableItemDeleteInfoModal
            isOpen={deleteInfoModalOpen}
            onClose={handleCloseDeleteInfo}
            itemName={itemToDelete?.item?.name || 'this item'}
            metricLabels={itemToDelete?.forecast_settings_metric_labels || []}
            totalNameLabels={itemToDelete?.profit_and_loss_total_name_labels || []}
          />
        </div>
      </Drawer>
    </DndProvider>
  );
};
